
import React from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { AppLayout } from '@/components/layout/AppLayout';
import { JourneyHero } from '@/components/journey/JourneyHero';
import { LiveMarketDashboard } from '@/components/dashboard/LiveMarketDashboard';
import { RecentActivity } from '@/components/journey/RecentActivity';
import { AvailableTools } from '@/components/journey/AvailableTools';
import { InvestorToolsQuickAccess } from '@/components/dashboard/InvestorToolsQuickAccess';
import { MotionBackground } from '@/components/futuristic/MotionBackground';
import { EquiForgeLogo } from '@/components/branding';

const Dashboard: React.FC = () => {
  const { user } = useAuth();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.95, rotateX: 15 },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      rotateX: 0,
      transition: { 
        duration: 0.8,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  return (
    <AppLayout
      headerTitle=""
      showBackButton={false}
      globalPageTitle="Dashboard"
      showGlobalHeader={true}
    >
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 relative overflow-hidden">
        {/* Motion Background */}
        <MotionBackground />

        <motion.div 
          className="container py-8 max-w-7xl space-y-8 relative z-10"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Hero Branding Header */}
          <motion.div variants={itemVariants}>
            <div className="w-full mb-8">
              <div className="w-full p-10 md:p-12 lg:p-16 bg-gradient-to-br from-yellow-400/10 via-purple-500/10 to-cyan-500/5 
                              backdrop-blur-md border border-yellow-400/20 rounded-2xl shadow-2xl">
                <div className="flex flex-col items-center space-y-4 overflow-visible">
                  {/* Large 3D Logo */}
                  <EquiForgeLogo size="xxxl" variant="compact" showText={false} />
                  
                  {/* Brand Name */}
                  <h1 className="text-7xl md:text-8xl lg:text-9xl font-bold bg-gradient-to-r 
                                 from-yellow-400 via-yellow-200 to-purple-400 
                                 bg-clip-text text-transparent 
                                 leading-tight py-2 overflow-visible">
                    EquiForge™
                  </h1>
                  
                  {/* Tagline */}
                  <p className="text-slate-300 text-base md:text-lg lg:text-xl text-center">
                    Advanced Mortgage Analytics Platform
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* 1. Available Tools - Full width section */}
          <motion.div variants={itemVariants}>
            <AvailableTools />
          </motion.div>

          {/* Investor Tools Quick Access */}
          <motion.div variants={itemVariants}>
            <InvestorToolsQuickAccess />
          </motion.div>

          {/* 2. Journey Hero - Primary focus showing current step and clear next action */}
          <motion.div variants={itemVariants}>
            <JourneyHero />
          </motion.div>

          {/* 3. Live Market Data - Real-time financial rates and market statistics */}
          <motion.div variants={itemVariants}>
            <LiveMarketDashboard />
          </motion.div>

          {/* 4. Recent Activity - What they've done recently */}
          <motion.div variants={itemVariants}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <RecentActivity />
              
              {/* Future: Add recommendations or tips here */}
              <div className="p-6 bg-background/30 backdrop-blur-sm border border-border/30 rounded-lg">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="space-y-4"
                >
                  <h2 className="text-lg font-semibold text-foreground">What's Next?</h2>
                  <div className="space-y-3">
                    <div className="p-3 bg-cyan-500/10 border border-cyan-500/20 rounded-lg">
                      <p className="text-sm text-foreground font-medium">💡 Pro Tip</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Complete your financial assessment to get personalized property recommendations
                      </p>
                    </div>
                    <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                      <p className="text-sm text-foreground font-medium">🎯 Goal</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Find your perfect home within your budget and preferences
                      </p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>

          {/* 5. Footer Branding - Simplified and smaller */}
          <motion.div 
            variants={itemVariants}
            className="text-center py-4"
          >
            <div className="p-4 bg-background/20 backdrop-blur-sm border border-border/20 rounded-lg">
              <p className="text-muted-foreground text-xs">
                Powered by <span className="text-cyan-400 font-semibold bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">EquiForge™</span> - 
                Intelligent homebuying platform
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </AppLayout>
  );
};

export default Dashboard;
